/* eslint-disable @typescript-eslint/ban-types */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';

import {
  ANSWER_CONFIG_TYPE,
  AnswerConfigType,
  ORIGINAL_FORM_VALIDATION,
  QUESTION_TYPE_ID,
} from '~/common/constants';
import { MultipleAnswerConfigAnswerProps, MultipleAnswerConfigProps } from '~/common/types';
import SortableItem from './SortableItem';
import AnswerControl from '../AnswerControl';

type Props = {
  questionIdx: number;
  totalQuestions: number;
  isQuestionValid?: boolean;
  handleValidation: Function;
  handleAnswerChange: Function;
  multipleAnswerConfig: MultipleAnswerConfigProps;
  isValidate?: boolean;
  handleFormValidationError: Function;
};

const MultipleAnswer: React.FC<Props> = ({
  questionIdx,
  totalQuestions,
  isQuestionValid,
  handleValidation,
  handleAnswerChange,
  multipleAnswerConfig,
  isValidate,
  handleFormValidationError,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const [answersListComponents, setAnswersListComponents] = useState(multipleAnswerConfig.answers);

  const addAnswer = (answerType: AnswerConfigType) => {
    const newAnswersListComponents = [...answersListComponents];

    if (newAnswersListComponents.length + 1 > ORIGINAL_FORM_VALIDATION.ANSWER_MAX_SIZE) {
      handleFormValidationError(t('registration.originalForm.validationErrorModal.maxAnswer'));
      return;
    }

    const newAnswerConfig = { ...multipleAnswerConfig };

    // Find index other answer and exclusive answer if exist
    const existOtherAnswer = newAnswersListComponents.findIndex(
      (answer) => answer.isOtherAnswer === true
    );
    const existExclusiveAnswer = newAnswersListComponents.findIndex(
      (answer) => answer.isExclusiveAnswer === true
    );

    if (answerType === ANSWER_CONFIG_TYPE.OTHER_REQUIRED) {
      if (existOtherAnswer < 0) {
        const newAnswer = {
          label: t('registration.originalForm.manual.answerLabel.otherAnswerRequired'),
          isValid: true,
          isOtherAnswer: true,
        };

        if (existExclusiveAnswer < 0) {
          newAnswersListComponents.push(newAnswer);
        } else {
          newAnswersListComponents.splice(existExclusiveAnswer, 0, newAnswer);
        }
      }
    } else if (answerType === ANSWER_CONFIG_TYPE.EXCLUSIVE_REQUIRED) {
      if (existExclusiveAnswer < 0) {
        newAnswersListComponents.push({
          label: t('registration.originalForm.manual.answerLabel.exclusiveAnswerRequired'),
          isValid: true,
          isExclusiveAnswer: true,
        });
      }
    } else {
      const newAnswer = {
        label: '',
        isValid: true,
      };

      if (existOtherAnswer >= 0) {
        newAnswersListComponents.splice(existOtherAnswer, 0, newAnswer);
      } else if (existExclusiveAnswer >= 0) {
        newAnswersListComponents.splice(existExclusiveAnswer, 0, newAnswer);
      } else {
        newAnswersListComponents.push(newAnswer);
      }
    }

    setAnswersListComponents(newAnswersListComponents);
    handleAnswerChange(newAnswersListComponents, newAnswerConfig);
  };

  /* istanbul ignore next */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over?.id && active.id !== over.id) {
      const getNewAnswerListComponents = (items: MultipleAnswerConfigAnswerProps[]) => {
        const jsonItems = items.map((item, idx) => idx + JSON.stringify(item));
        const activeIndex = jsonItems.indexOf(active.id as string);
        const overIndex = jsonItems.indexOf(over.id as string);

        return arrayMove(items, activeIndex, overIndex);
      };

      const updatedAnswerList = getNewAnswerListComponents(answersListComponents);
      setAnswersListComponents(updatedAnswerList);
      handleAnswerChange(updatedAnswerList, { ...multipleAnswerConfig });
    }
  };

  useEffect(() => {
    const checkInvalid = answersListComponents.find((el) => el.isValid === false);
    handleValidation(
      isQuestionValid === undefined ? true : isQuestionValid && checkInvalid === undefined
    );

    // const newAnswerConfig = { ...multipleAnswerConfig };
    // handleAnswerChange(answersListComponents, newAnswerConfig);
  }, [answersListComponents]);

  useEffect(() => {
    if (isValidate !== undefined) {
      setAnswersListComponents(multipleAnswerConfig.answers);
    }
  }, [multipleAnswerConfig.answers]);

  return (
    <>
      <div>
        <DndContext
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          collisionDetection={closestCenter}
          onDragEnd={/* istanbul ignore next */ handleDragEnd}
        >
          <SortableContext
            items={answersListComponents.map((sortItem, idx) => idx + JSON.stringify(sortItem))}
            strategy={verticalListSortingStrategy}
          >
            {answersListComponents.map((item, answerIdx) => (
              <SortableItem
                key={answerIdx}
                id={answerIdx + JSON.stringify(item)}
                questionIdx={questionIdx}
                totalQuestions={totalQuestions}
                answerIdx={answerIdx}
                handleAnswerChange={handleAnswerChange}
                isValidate={isValidate}
                item={item}
                answersListComponents={answersListComponents}
                setAnswersListComponents={setAnswersListComponents}
                handleFormValidationError={handleFormValidationError}
                multipleAnswerConfig={multipleAnswerConfig}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      <AnswerControl
        questionType={QUESTION_TYPE_ID.MULTIPLE_ANSWER}
        handleAddAnswer={addAnswer}
        answersComponentLength={answersListComponents.length}
      />
    </>
  );
};
export default MultipleAnswer;
